import React, { useState, useEffect } from 'react';
import { Table, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { PageTitle } from '@/shared/components/PageTitle';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/shared/lib/auth-context';
import { useGetApiAdminSearchHistory } from '@/features/guides/api/generated';
import type { SearchHistorySummary, GetApiAdminSearchHistorySort } from '@/features/guides/api/generated';
import { useTranslation } from 'react-i18next';
import { SearchViewsDrawer } from '@/features/guides/components/SearchViewsDrawer';

interface SearchHistoryTableData extends SearchHistorySummary {
  key: string;
}

type SortField = 'requestedAt' | 'locale' | 'resultCount';
type SortOrder = 'asc' | 'desc';

const formatStockholmTime = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

export const SearchHistoryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  // Initialize state from URL params
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('pageSize')) || 100;
  const initialSortField = (searchParams.get('sortField') as SortField) || 'requestedAt';
  const initialSortOrder = (searchParams.get('sortOrder') as SortOrder) || 'desc';

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortField, setSortField] = useState<SortField | null>(initialSortField);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    if (currentPage !== 1) {
      newParams.set('page', String(currentPage));
    }
    if (pageSize !== 100) {
      newParams.set('pageSize', String(pageSize));
    }
    if (sortField !== 'requestedAt' || sortOrder !== 'desc') {
      if (sortField) {
        newParams.set('sortField', sortField);
        newParams.set('sortOrder', sortOrder);
      }
    }

    if (searchParams.toString() !== newParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [currentPage, pageSize, sortField, sortOrder, searchParams, setSearchParams]);

  // Build sort parameter according to API spec: "field:direction"
  const sortParam = sortField ? `${sortField}:${sortOrder}` as GetApiAdminSearchHistorySort : undefined;

  const {
    data: searchHistoryResponse,
    isLoading,
  } = useGetApiAdminSearchHistory(
    {
      Page: currentPage,
      PageSize: pageSize,
      ...(sortParam && { Sort: sortParam }),
    },
    {
      query: {
        enabled: !!token && isAuthenticated && !authLoading,
      },
    }
  );

  const columns: ColumnsType<SearchHistoryTableData> = [
    {
      title: t('searchHistory.requestedAt'),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 180,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: sortField === 'requestedAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      showSorterTooltip: false,
      render: (date: string) => date ? formatStockholmTime(date) : '-',
    },
    {
      title: t('searchHistory.problem'),
      dataIndex: 'problem',
      key: 'problem',
      width: 400,
      render: (text: string) => (
        <Tooltip title={text}>
          <div style={{
            maxWidth: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('searchHistory.userName'),
      dataIndex: ['user', 'name'],
      key: 'userName',
      width: 200,
      render: (name: string) => name || '-',
    },
    {
      title: t('searchHistory.locale'),
      dataIndex: 'locale',
      key: 'locale',
      width: 100,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: sortField === 'locale' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      showSorterTooltip: false,
      render: (locale: string) => (
        <Tag color="blue">{locale?.toUpperCase()}</Tag>
      ),
    },
    {
      title: t('searchHistory.resultCount'),
      dataIndex: 'resultCount',
      key: 'resultCount',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: sortField === 'resultCount' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      showSorterTooltip: false,
      align: 'center',
    },
    {
      title: t('searchHistory.vehicle'),
      key: 'vehicle',
      width: 300,
      render: (_, record) => {
        const vehicle = record.vehicle;
        if (!vehicle) return '-';

        const parts = [
          vehicle.manufacturer,
          vehicle.modelSeries,
          vehicle.modelYear,
          vehicle.engineBaseType,
        ].filter(Boolean);

        return parts.length > 0 ? parts.join(' ') : '-';
      },
    },
  ];

  const tableData: SearchHistoryTableData[] = (searchHistoryResponse?.searches || []).map(search => ({
    ...search,
    key: search.id || Math.random().toString(),
  }));

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, unknown>,
    sorter: SorterResult<SearchHistoryTableData> | SorterResult<SearchHistoryTableData>[]
  ) => {
    // Handle pagination
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);

    // Handle sorting - only process single column sort
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const field = sorter.columnKey as SortField;

      if (sorter.order === 'ascend') {
        setSortField(field);
        setSortOrder('asc');
      } else if (sorter.order === 'descend') {
        setSortField(field);
        setSortOrder('desc');
      } else {
        setSortField(field);
        setSortOrder('desc');
      }
    }
  };

  const handleRowClick = (record: SearchHistoryTableData) => {
    setSelectedSearchId(record.id || null);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedSearchId(null);
  };

  return (
    <div>
      <PageTitle
        title={t('searchHistory.title')}
        subtitle={t('searchHistory.subtitle')}
      />

      <Table
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: searchHistoryResponse?.totalCount || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('searchHistory.totalSearches', { start: range[0], end: range[1], total }),
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 1200 }}
      />

      <SearchViewsDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        searchId={selectedSearchId}
      />
    </div>
  );
};
